import React, { useState } from 'react';
import { linkHorizontal } from 'd3';
import { GraphLink } from '../types';
import css from '../index.module.scss';
import { BOX_WIDTH, BOX_LEFT_RIGHT_MARGIN, JOINT_WIDTH } from '../const';
import { Box, SVGTooltip, Typography } from '@fullstory/components';
import { VirtualElement } from '@popperjs/core';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { classNames } from '@fullstory/util/fmt';
import { useDebounce } from '@fullstory/react-hooks';
import { useIntl } from 'react-intl';

export interface LinkProps {
  link: GraphLink;
  offset: number;
  sourceOffset: number;
  targetOffset: number;
  virtualAnchor: VirtualElement;
  onMouseMove: (e: React.MouseEvent) => void;
  inSameSession: boolean;
  direction: JourneyMapSettings.Direction;
  isIntelligentPath?: boolean;
}

export const Link: React.FC<LinkProps> = ({
  link,
  sourceOffset,
  targetOffset,
  offset,
  virtualAnchor,
  inSameSession,
  direction,
  onMouseMove,
  isIntelligentPath = false,
}) => {
  const path = linkHorizontal<GraphLink, [number, number]>()
    .source(d => [
      d.source.x + BOX_WIDTH + BOX_LEFT_RIGHT_MARGIN + JOINT_WIDTH + offset,
      sourceOffset + d.source.y + d.height / 2,
    ])
    .target(d => [
      d.target.x - BOX_LEFT_RIGHT_MARGIN - JOINT_WIDTH + offset,
      targetOffset + d.target.y + d.height / 2,
    ]);

  const [emphasize, setEmphasize] = useState(false);
  const debouncedEmphasize = useDebounce(emphasize, 100);

  const _setEmphasizeTrue = () => setEmphasize(true);
  const _setEmphasizeFalse = () => setEmphasize(false);

  return (
    <>
      <NoodleTooltip
        inSameSession={inSameSession}
        value={link.value}
        direction={direction}
        virtualAnchor={virtualAnchor}
      >
        <EndPoint
          height={link.height}
          onMouseMove={onMouseMove}
          setEmphasizeFalse={_setEmphasizeFalse}
          setEmphasizeTrue={_setEmphasizeTrue}
          x={link.source.x + BOX_WIDTH + BOX_LEFT_RIGHT_MARGIN + offset}
          y={link.source.y + sourceOffset}
          isIntelligentPath={isIntelligentPath}
        />
      </NoodleTooltip>
      <NoodleTooltip
        inSameSession={inSameSession}
        value={link.value}
        direction={direction}
        virtualAnchor={virtualAnchor}
      >
        <path
          d={path(link) ?? undefined}
          className={classNames(css.path, {
            [css.emphasis]: debouncedEmphasize,
            [css.intelligence]: isIntelligentPath,
          })}
          strokeWidth={link.height ?? 0}
          opacity={0.6}
          onMouseMove={onMouseMove}
          onMouseEnter={_setEmphasizeTrue}
          onMouseOver={_setEmphasizeTrue}
          onMouseOut={_setEmphasizeFalse}
          onFocus={_setEmphasizeTrue}
          onBlur={_setEmphasizeFalse}
        />
      </NoodleTooltip>
      <NoodleTooltip
        inSameSession={inSameSession}
        value={link.value}
        direction={direction}
        virtualAnchor={virtualAnchor}
      >
        <EndPoint
          height={link.height}
          onMouseMove={onMouseMove}
          setEmphasizeFalse={_setEmphasizeFalse}
          setEmphasizeTrue={_setEmphasizeTrue}
          x={link.target.x - BOX_LEFT_RIGHT_MARGIN - JOINT_WIDTH + offset}
          y={link.target.y + targetOffset}
          isIntelligentPath={isIntelligentPath}
        />
      </NoodleTooltip>
    </>
  );
};

interface EndPointProps {
  height: number;
  onMouseMove: (e: React.MouseEvent) => void;
  setEmphasizeFalse: (() => void) | undefined;
  setEmphasizeTrue: (() => void) | undefined;
  x: number;
  y: number;
  isIntelligentPath?: boolean;
}

function EndPoint({
  height,
  onMouseMove,
  setEmphasizeFalse,
  setEmphasizeTrue,
  x,
  y,
  isIntelligentPath = false,
}: EndPointProps) {
  return (
    <rect
      data-component="EndPoint"
      x={x}
      y={y}
      width={JOINT_WIDTH}
      height={height}
      className={classNames(css.endPoint, { [css.intelligence]: isIntelligentPath })}
      onMouseMove={onMouseMove}
      onMouseEnter={setEmphasizeTrue}
      onMouseOver={setEmphasizeTrue}
      onMouseOut={setEmphasizeFalse}
      onFocus={setEmphasizeTrue}
      onBlur={setEmphasizeFalse}
    />
  );
}

interface NoodleTooltipProps {
  children: React.ReactNode;
  inSameSession: boolean;
  value: number;
  direction: JourneyMapSettings.Direction;
  virtualAnchor: VirtualElement;
}

function NoodleTooltip({
  inSameSession,
  value,
  direction,
  children,
  virtualAnchor,
}: NoodleTooltipProps) {
  const intl = useIntl();
  return (
    <SVGTooltip
      data-component="NoodleTooltip"
      tooltipContent={
        <>
          <Box>
            <Typography variant="body3" fontWeight="regular" color="neutrals-n0">
              {direction === JourneyMapSettings.Direction.DIRECTION_FORWARD
                ? intl.formatMessage({ defaultMessage: 'Continue', id: 'acrOoz' })
                : intl.formatMessage({ defaultMessage: 'Previous', id: 'JJNc3c' })}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body3" fontWeight="regular" color="neutrals-n0">
              {inSameSession
                ? intl.formatMessage(
                    {
                      defaultMessage:
                        '{numSessions, number, ::compact-short} {numSessions, plural, one {Session} other {Sessions}}',
                      id: 'sFghlG',
                    },
                    { numSessions: value },
                  )
                : intl.formatMessage(
                    {
                      defaultMessage:
                        '{numSessions, number, ::compact-short} {numSessions, plural, one {User} other {Users}}',
                      id: 'nGc6/+',
                    },
                    { numSessions: value },
                  )}
            </Typography>
          </Box>
        </>
      }
      virtualAnchor={virtualAnchor}
      offset={10}
    >
      {children}
    </SVGTooltip>
  );
}
